import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Briefcase, Scale, Wrench, IndianRupee,
  Zap, Link2, CheckCheck, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_SEQUENCE = [
  { key: 'marketing', label: 'Marketing',  Icon: Briefcase,    color: '#0d9488' },
  { key: 'legal',     label: 'Legal',      Icon: Scale,        color: '#9333ea' },
  { key: 'technical', label: 'Technical',  Icon: Wrench,       color: '#ea580c' },
  { key: 'finance',   label: 'Finance',    Icon: IndianRupee,  color: '#16a34a' },
];

const fmtAmt = (v) => {
  const n = Number(v);
  if (!v || isNaN(n) || n === 0) return null;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

/**
 * PromoteStagesModal
 *
 * Props:
 *   deal            — the deal object { id, sheet, customer_name, barter_pct, expected_amount, dealGroupId, ... }
 *   isOpen          — boolean
 *   onClose         — () => void
 *   onSuccess       — (responseData) => void  — called after a successful promotion
 *   existingSheets  — string[]  — sheets where this deal's group already exists (grayed out in UI)
 */
const PromoteStagesModal = ({ deal, isOpen, onClose, onSuccess, existingSheets = [] }) => {
  const [selected, setSelected]       = useState([]);
  const [promoting, setPromoting]     = useState(false);
  const [resolvedSheets, setResolved] = useState(existingSheets); // sheets that already have this group

  const token   = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  // When modal opens, if deal has a dealGroupId but parent only passed source sheet,
  // fetch the actual group membership so we can show correct LINKED badges.
  useEffect(() => {
    if (!isOpen || !deal) return;

    const hasGroupId = !!deal.dealGroupId;
    // If parent passed full group data (>1 sheet), trust it; otherwise fetch
    const needsFetch = hasGroupId && existingSheets.length <= 1;

    if (needsFetch) {
      axios.get(`${API}/barter/pipeline`, { headers })
        .then(res => {
          const groupSheets = Object.keys(res.data?.groups?.[deal.dealGroupId] || {});
          setResolved(groupSheets.length > 0 ? groupSheets : existingSheets);
        })
        .catch(() => setResolved(existingSheets));
    } else {
      setResolved(existingSheets);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deal]);

  // Pre-select all available stages after resolvedSheets is set
  useEffect(() => {
    if (!isOpen || !deal) return;
    const available = STAGE_SEQUENCE
      .filter(s => s.key !== deal.sheet && !resolvedSheets.includes(s.key))
      .map(s => s.key);
    setSelected(available);
  }, [isOpen, deal, resolvedSheets]);

  const allAvailable = STAGE_SEQUENCE.filter(
    s => s.key !== deal?.sheet && !resolvedSheets.includes(s.key)
  );
  const allSelected = allAvailable.every(s => selected.includes(s.key));

  const toggle = (key) =>
    setSelected(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );

  const selectAll = () => setSelected(allAvailable.map(s => s.key));
  const deselectAll = () => setSelected([]);

  const handlePromote = async () => {
    if (selected.length === 0) {
      toast.warning('Select at least one stage to promote to.');
      return;
    }
    setPromoting(true);
    try {
      const res = await axios.post(
        `${API}/barter/deals/${deal.id}/promote-all`,
        { target_sheets: selected },
        { headers }
      );
      const n = res.data.created?.length ?? 0;
      const s = res.data.skipped_existing?.length ?? 0;
      const msg = s > 0
        ? `Promoted to ${n} stage${n !== 1 ? 's' : ''}! (${s} already existed)`
        : `Promoted to ${n} stage${n !== 1 ? 's' : ''}!`;
      toast.success(msg);
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Promotion failed');
    } finally {
      setPromoting(false);
    }
  };

  if (!deal) return null;

  const amt     = fmtAmt(deal.expected_amount);
  const srcMeta = STAGE_SEQUENCE.find(s => s.key === deal.sheet);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="promote-stages-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <span className="w-6 h-6 bg-amber-500 rounded flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-white" />
            </span>
            Promote Deal to Stages
          </DialogTitle>
        </DialogHeader>

        {/* Deal summary */}
        <div className="px-1 py-2 bg-stone-50 rounded-lg border border-stone-200 text-xs">
          <div className="font-bold text-stone-900 mb-0.5">{deal.customer_name || '(No Name)'}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-stone-500">
            {deal.dm_name && <span>DM: <b className="text-stone-700">{deal.dm_name}</b></span>}
            {(deal.barter_pct !== undefined && deal.barter_pct !== null && deal.barter_pct !== '') && (
              <span>Barter: <b className="text-stone-800">{deal.barter_pct}%</b></span>
            )}
            {amt && <span>Expected: <b className="text-emerald-700">{amt}</b></span>}
            {srcMeta && (
              <span>Source: <b style={{ color: srcMeta.color }}>{srcMeta.label.toUpperCase()}</b></span>
            )}
          </div>
        </div>

        {/* Stage selection */}
        <div className="space-y-1" data-testid="stage-selection-list">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-stone-600">Select target stages:</p>
            {allAvailable.length > 0 && (
              <button
                onClick={allSelected ? deselectAll : selectAll}
                className="text-[10px] text-stone-400 hover:text-stone-700 font-medium underline underline-offset-2"
                data-testid="select-all-stages-btn"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          {STAGE_SEQUENCE.map(stage => {
            const isSource  = stage.key === deal.sheet;
            const isLinked  = resolvedSheets.includes(stage.key) && !isSource;
            const isDisabled = isSource || isLinked;
            const isChecked  = isSource || isLinked || selected.includes(stage.key);
            const Icon = stage.Icon;

            return (
              <label
                key={stage.key}
                data-testid={`stage-option-${stage.key}`}
                className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer
                  ${isDisabled
                    ? 'bg-stone-50 border-stone-200 opacity-60 cursor-not-allowed'
                    : isChecked
                      ? 'bg-white border-stone-400 shadow-sm'
                      : 'bg-white border-stone-200 hover:border-stone-300'
                  }`}
              >
                <Checkbox
                  checked={isChecked}
                  disabled={isDisabled}
                  onCheckedChange={() => !isDisabled && toggle(stage.key)}
                  className={isDisabled ? 'opacity-50' : ''}
                  data-testid={`stage-checkbox-${stage.key}`}
                />

                {/* Icon */}
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: isDisabled ? `${stage.color}30` : `${stage.color}20`,
                    color: stage.color,
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-stone-800">{stage.label}</div>
                  {isSource && (
                    <div className="text-[10px] text-blue-600 font-medium">Source — this deal's origin</div>
                  )}
                  {isLinked && (
                    <div className="text-[10px] text-emerald-600 font-medium flex items-center gap-0.5">
                      <Link2 className="w-2.5 h-2.5" /> Already linked
                    </div>
                  )}
                  {!isDisabled && (
                    <div className="text-[10px] text-stone-400">
                      {selected.includes(stage.key) ? 'Will be promoted' : 'Click to include'}
                    </div>
                  )}
                </div>

                {/* Right badge */}
                {isSource && (
                  <span className="text-[9px] bg-blue-100 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full font-bold shrink-0">
                    SOURCE
                  </span>
                )}
                {isLinked && (
                  <span className="text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5 shrink-0">
                    <Link2 className="w-2 h-2" /> LINKED
                  </span>
                )}
              </label>
            );
          })}
        </div>

        {/* Empty state: fully promoted */}
        {allAvailable.length === 0 && (
          <div className="text-center py-3 text-xs text-stone-400">
            <CheckCheck className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
            This deal is already linked to all 4 stages.
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-1 gap-2">
          <Button
            variant="outline" size="sm"
            onClick={onClose}
            disabled={promoting}
            className="text-xs"
            data-testid="promote-modal-cancel"
          >
            Cancel
          </Button>

          <Button
            size="sm"
            onClick={handlePromote}
            disabled={promoting || selected.length === 0}
            className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-4"
            data-testid="promote-modal-confirm"
          >
            {promoting
              ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Promoting…</>
              : <><Zap className="w-3 h-3 mr-1" /> Promote to {selected.length} Stage{selected.length !== 1 ? 's' : ''}</>
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PromoteStagesModal;
